import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import api from '@/lib/api'
import type { ManualHolding } from '@/types'

type TickerSearchResult = {
  ticker: string
  name: string
  has_logo?: boolean
}

function TickerLogo({ ticker, hasLogo }: { ticker: string; hasLogo?: boolean }) {
  const [hasError, setHasError] = useState(false)
  const fallbackLetter = ticker.charAt(0) || '?'

  useEffect(() => {
    setHasError(false)
  }, [ticker])

  if (!hasLogo || hasError) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        {fallbackLetter}
      </div>
    )
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden">
      <img
        src={`/api/investments/logo/${ticker}`}
        alt={`${ticker} logo`}
        className="h-10 w-10 object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  )
}

const emptyForm = {
  ticker: '',
  name: '',
  quantity: '',
  price: '',
  costBasis: '',
}

interface HoldingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: number
  holding?: ManualHolding | null
  onSaved: () => void
}

export function HoldingDialog({
  open,
  onOpenChange,
  accountId,
  holding,
  onSaved,
}: HoldingDialogProps) {
  const isEditing = Boolean(holding)
  const [form, setForm] = useState(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [tickerResults, setTickerResults] = useState<TickerSearchResult[]>([])
  const [isTickerSearching, setIsTickerSearching] = useState(false)
  const [tickerSearchError, setTickerSearchError] = useState<string | null>(null)
  const [showTickerDropdown, setShowTickerDropdown] = useState(false)
  const [isTickerLookupLoading, setIsTickerLookupLoading] = useState(false)
  const tickerSearchTimeoutRef = useRef<number | null>(null)
  const tickerQueryRef = useRef('')
  const tickerAutocompleteRef = useRef<HTMLDivElement | null>(null)
  const lastLookupTickerRef = useRef<string | null>(null)

  // Initialize the form whenever the dialog opens.
  useEffect(() => {
    if (!open) {
      return
    }
    if (holding) {
      setForm({
        ticker: holding.ticker,
        name: holding.name,
        quantity: String(holding.quantity),
        price: String(holding.price),
        costBasis: holding.cost_basis != null ? String(holding.cost_basis) : '',
      })
      // Don't auto-overwrite an existing holding's price on blur unless the ticker changes.
      lastLookupTickerRef.current = holding.ticker.toUpperCase()
    } else {
      setForm(emptyForm)
      lastLookupTickerRef.current = null
    }
    setFormError(null)
    setIsSaving(false)
    setTickerResults([])
    setShowTickerDropdown(false)
    setTickerSearchError(null)
  }, [open, holding])

  useEffect(() => {
    tickerQueryRef.current = form.ticker.trim()
  }, [form.ticker])

  // Debounced ticker search.
  useEffect(() => {
    if (!open) {
      return
    }

    const query = form.ticker.trim()
    if (tickerSearchTimeoutRef.current) {
      window.clearTimeout(tickerSearchTimeoutRef.current)
      tickerSearchTimeoutRef.current = null
    }

    if (!query) {
      setTickerResults([])
      setShowTickerDropdown(false)
      setIsTickerSearching(false)
      setTickerSearchError(null)
      return
    }

    setIsTickerSearching(true)
    setTickerSearchError(null)
    setShowTickerDropdown(true)

    tickerSearchTimeoutRef.current = window.setTimeout(async () => {
      try {
        const response = await api.get('/stock/search', {
          params: { q: query, limit: 10 },
        })
        if (tickerQueryRef.current !== query) {
          return
        }
        setTickerResults(response.data.results || [])
        setShowTickerDropdown(true)
      } catch (error) {
        if (tickerQueryRef.current !== query) {
          return
        }
        setTickerResults([])
        setTickerSearchError('Error searching tickers.')
      } finally {
        if (tickerQueryRef.current === query) {
          setIsTickerSearching(false)
        }
      }
    }, 300)

    return () => {
      if (tickerSearchTimeoutRef.current) {
        window.clearTimeout(tickerSearchTimeoutRef.current)
        tickerSearchTimeoutRef.current = null
      }
    }
  }, [form.ticker, open])

  // Close the autocomplete dropdown on outside click.
  useEffect(() => {
    if (!showTickerDropdown) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      if (!tickerAutocompleteRef.current) {
        return
      }
      if (!tickerAutocompleteRef.current.contains(event.target as Node)) {
        setShowTickerDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [showTickerDropdown])

  const clearTickerAutocomplete = () => {
    setTickerResults([])
    setShowTickerDropdown(false)
    setIsTickerSearching(false)
    setTickerSearchError(null)
  }

  const lookupStockInfo = useCallback(async (rawTicker: string) => {
    const normalized = rawTicker.trim().toUpperCase()
    if (!normalized || lastLookupTickerRef.current === normalized) {
      return
    }

    setIsTickerLookupLoading(true)
    try {
      const response = await api.get(`/stock/lookup/${encodeURIComponent(normalized)}`)
      const price = Number(response.data?.price)
      setForm((prev) => ({
        ...prev,
        ticker: normalized,
        name: response.data?.name || prev.name || normalized,
        price: Number.isFinite(price) ? price.toFixed(2) : prev.price,
      }))
      setFormError(null)
      lastLookupTickerRef.current = normalized
    } catch (error) {
      console.error('Failed to lookup stock:', error)
    } finally {
      setIsTickerLookupLoading(false)
    }
  }, [])

  const handleTickerSelect = (result: TickerSearchResult) => {
    setForm((prev) => ({
      ...prev,
      ticker: result.ticker.toUpperCase(),
      name: result.name || prev.name,
    }))
    setFormError(null)
    clearTickerAutocomplete()
    lookupStockInfo(result.ticker)
  }

  const handleTickerBlur = () => {
    window.setTimeout(() => {
      clearTickerAutocomplete()
      lookupStockInfo(form.ticker)
    }, 150)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setFormError(null)

    const ticker = form.ticker.trim().toUpperCase()
    const name = form.name.trim()
    const quantity = Number(form.quantity)
    const price = Number(form.price)
    const costBasisInput = form.costBasis.trim()

    if (!ticker || !name) {
      setFormError('Ticker and name are required.')
      return
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setFormError('Quantity must be greater than zero.')
      return
    }
    if (!Number.isFinite(price) || price <= 0) {
      setFormError('Price must be greater than zero.')
      return
    }
    let costBasis: number | null = null
    if (costBasisInput) {
      costBasis = Number(costBasisInput)
      if (!Number.isFinite(costBasis)) {
        setFormError('Cost basis must be a number.')
        return
      }
    }

    const payload = { ticker, name, quantity, price, cost_basis: costBasis }

    setIsSaving(true)
    try {
      if (holding) {
        await api.put(`/manual_accounts/${accountId}/holdings/${holding.id}`, payload)
      } else {
        await api.post(`/manual_accounts/${accountId}/holdings`, payload)
      }
      onSaved()
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save holding:', error)
      setFormError('Unable to save holding. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Holding' : 'Add Holding'}</DialogTitle>
          <DialogDescription>
            Track a position in this manual investment account.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="holding-ticker">Ticker</Label>
              <div className="relative" ref={tickerAutocompleteRef}>
                <Input
                  id="holding-ticker"
                  value={form.ticker}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      ticker: event.target.value.toUpperCase(),
                    }))
                    setFormError(null)
                    lastLookupTickerRef.current = null
                  }}
                  onBlur={handleTickerBlur}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      clearTickerAutocomplete()
                    }
                  }}
                  placeholder="AAPL"
                  autoComplete="off"
                />
                {showTickerDropdown && (
                  <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    {isTickerSearching ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        Searching...
                      </div>
                    ) : tickerSearchError ? (
                      <div className="px-3 py-2 text-sm text-danger">
                        {tickerSearchError}
                      </div>
                    ) : tickerResults.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No results found.
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-y-auto">
                        {tickerResults.map((result) => (
                          <button
                            key={`${result.ticker}-${result.name}`}
                            type="button"
                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-gray-50 dark:hover:bg-gray-800"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleTickerSelect(result)}
                          >
                            <TickerLogo
                              ticker={result.ticker}
                              hasLogo={result.has_logo}
                            />
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {result.ticker}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {result.name}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="holding-name">Name</Label>
              <Input
                id="holding-name"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder={isTickerLookupLoading ? 'Loading...' : 'Apple Inc.'}
                disabled={isTickerLookupLoading}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="holding-quantity">Quantity</Label>
              <Input
                id="holding-quantity"
                type="number"
                min="0"
                step="any"
                value={form.quantity}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, quantity: event.target.value }))
                }
                placeholder="10"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="holding-price">Price</Label>
              <Input
                id="holding-price"
                type="number"
                min="0"
                step="any"
                value={form.price}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, price: event.target.value }))
                }
                placeholder={isTickerLookupLoading ? 'Loading...' : '195.50'}
                disabled={isTickerLookupLoading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="holding-cost-basis">Cost basis</Label>
              <Input
                id="holding-cost-basis"
                type="number"
                min="0"
                step="any"
                value={form.costBasis}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, costBasis: event.target.value }))
                }
                placeholder="1955.00"
              />
            </div>
          </div>

          {formError && <p className="text-sm text-danger">{formError}</p>}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Add Holding'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
